import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { ApiService } from '../../../services/ApiService';

interface DocumentVersionUploadProps {
  documentId: string;
  documentTitle: string;
  currentVersion: number;
  onUploadSuccess?: (newVersion: number) => void;
  onCancel?: () => void;
}

const DocumentVersionUpload: React.FC<DocumentVersionUploadProps> = ({
  documentId,
  documentTitle,
  currentVersion,
  onUploadSuccess,
  onCancel,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [title, setTitle] = useState(documentTitle);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newVersion, setNewVersion] = useState<number | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!versionNotes.trim()) {
      setError('Please provide version notes describing the changes');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('versionNotes', versionNotes);
      if (title !== documentTitle) {
        formData.append('title', title);
      }

      // Create XMLHttpRequest to track upload progress
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (err) {
              reject(new Error('Invalid server response'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || 'Upload failed'));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });
      });

      // Get auth token
      const token = localStorage.getItem('token');
      
      // Open and send request
      xhr.open('POST', `/api/documents/${documentId}/versions`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);

      const response: any = await uploadPromise;
      
      setSuccess(true);
      setNewVersion(response.version);
      setUploadProgress(100);

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(response.version);
      }

      // Reset form after a delay
      setTimeout(() => {
        setFile(null);
        setVersionNotes('');
        setTitle(documentTitle);
        setSuccess(false);
        setUploadProgress(0);
      }, 2000);

    } catch (err: any) {
      console.error('Error uploading version:', err);
      setError(err.message || 'Failed to upload new version');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload New Version
      </Typography>
      
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Current version: {currentVersion}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          New version will be: {currentVersion + 1}
        </Typography>
      </Box>

      {/* Title field */}
      <TextField
        fullWidth
        label="Document Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        margin="normal"
        variant="outlined"
        helperText="You can update the document title with this version"
      />

      {/* File selection */}
      <Box my={3}>
        <input
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
          style={{ display: 'none' }}
          id="version-file-upload"
          type="file"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <label htmlFor="version-file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<UploadIcon />}
            disabled={uploading}
            fullWidth
          >
            {file ? file.name : 'Select File'}
          </Button>
        </label>
        {file && (
          <FormHelperText>
            Size: {(file.size / 1024 / 1024).toFixed(2)} MB
          </FormHelperText>
        )}
      </Box>

      {/* Version notes */}
      <TextField
        fullWidth
        label="Version Notes"
        value={versionNotes}
        onChange={(e) => setVersionNotes(e.target.value)}
        margin="normal"
        variant="outlined"
        multiline
        rows={3}
        placeholder="Describe what changed in this version..."
        required
        helperText="Required: Describe the changes made in this version"
      />

      {/* Upload progress */}
      {uploading && (
        <Box my={2}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="text.secondary" align="center" mt={1}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {/* Success message */}
      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ my: 2 }}
        >
          Version {newVersion} uploaded successfully!
        </Alert>
      )}

      {/* Action buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button
          onClick={handleCancel}
          disabled={uploading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || !versionNotes.trim() || uploading || success}
          startIcon={uploading ? null : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload New Version'}
        </Button>
      </Box>
    </Paper>
  );
};

export default DocumentVersionUpload;
